import React, { useState, useEffect, useRef } from 'react';
import { useMaintenanceStatus } from '../contexts/MaintenanceContext';
import { useNavigate } from 'react-router-dom';

/**
 * MaintenanceBanner - Global persistent banner shown when maintenance is scheduled.
 * Shows countdown timer using server-synced time.
 * Visible on ALL pages. Not permanently dismissible.
 */
const MaintenanceBanner = () => {
  const { hasUpcomingMaintenance, isInMaintenance, schedule, getServerNow } = useMaintenanceStatus();
  const [timeLeft, setTimeLeft] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const intervalRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!schedule || !schedule.start_time) {
      setTimeLeft(null);
      return;
    }

    const updateCountdown = () => {
      const now = getServerNow();
      const start = new Date(schedule.start_time);
      const diff = start - now;

      if (diff <= 0) {
        setTimeLeft(null);
        // Re-show banner if it was dismissed (maintenance started)
        setDismissed(false);
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeLeft({ hours, minutes, seconds, totalMs: diff });
    };

    updateCountdown();
    intervalRef.current = setInterval(updateCountdown, 1000);
    return () => clearInterval(intervalRef.current);
  }, [schedule, getServerNow]);

  // Re-show banner every 2 minutes if dismissed (not permanently dismissible)
  useEffect(() => {
    if (!dismissed) return;
    const timer = setTimeout(() => setDismissed(false), 2 * 60 * 1000);
    return () => clearTimeout(timer);
  }, [dismissed]);

  // Redirect to maintenance page if maintenance is active (skip all admin paths)
  useEffect(() => {
    if (isInMaintenance &&
        window.location.pathname !== '/maintenance' &&
        !window.location.pathname.startsWith('/admin')) {
      navigate('/maintenance');
    }
  }, [isInMaintenance, navigate]);

  if (!hasUpcomingMaintenance && !isInMaintenance) return null;
  if (dismissed && !isInMaintenance) return null;

  // Determine urgency level
  const isUrgent = timeLeft && timeLeft.totalMs <= 5 * 60 * 1000; // 5 minutes
  const isWarning = timeLeft && timeLeft.totalMs <= 15 * 60 * 1000; // 15 minutes

  const bannerStyle = {
    position: 'relative',
    zIndex: 1001,
    padding: '10px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    fontSize: '14px',
    fontWeight: 500,
    backgroundColor: isInMaintenance ? '#DC3545' : isUrgent ? '#DC3545' : isWarning ? '#FD7E14' : '#FFC107',
    color: isInMaintenance || isUrgent ? '#FFFFFF' : '#212529',
    borderBottom: '1px solid rgba(0,0,0,0.1)',
    flexWrap: 'wrap',
    textAlign: 'center',
  };

  const countdownStyle = {
    fontFamily: 'monospace',
    fontWeight: 700,
    fontSize: '15px',
    backgroundColor: 'rgba(0,0,0,0.15)',
    padding: '2px 8px',
    borderRadius: '4px',
  };

  const dismissBtnStyle = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '18px',
    lineHeight: 1,
    padding: '0 4px',
    color: 'inherit',
    opacity: 0.7,
  };

  if (isInMaintenance) {
    return (
      <div style={bannerStyle} role="alert">
        <strong>System Maintenance:</strong>
        <span>System is currently under maintenance. We'll be back shortly.</span>
      </div>
    );
  }

  const pad = (n) => String(n).padStart(2, '0');

  // Build the message based on urgency
  let bannerMessage;
  if (isUrgent) {
    bannerMessage = (
      <>
        <strong>System Maintenance:</strong>
        <span>System will go down in less than 5 minutes. Please save your work.</span>
      </>
    );
  } else if (isWarning) {
    bannerMessage = (
      <>
        <strong>System Maintenance:</strong>
        <span>Scheduled maintenance approaching. Please save your work.</span>
      </>
    );
  } else {
    bannerMessage = (
      <>
        <strong>Scheduled Maintenance:</strong>
        <span>{schedule?.title || 'System maintenance'}</span>
      </>
    );
  }

  return (
    <div style={bannerStyle} role="alert">
      {bannerMessage}
      {timeLeft && (
        <span style={countdownStyle}>
          {timeLeft.hours > 0 ? `${pad(timeLeft.hours)}:` : ''}{pad(timeLeft.minutes)}:{pad(timeLeft.seconds)}
        </span>
      )}
      {!isUrgent && (
        <button style={dismissBtnStyle} onClick={() => setDismissed(true)} title="Dismiss temporarily">
          &times;
        </button>
      )}
    </div>
  );
};

export default MaintenanceBanner;
